import React, { useEffect, useMemo, useRef, useState } from "react";
import { MdClose, MdKeyboardArrowDown, MdSearch } from "react-icons/md";
import "./LocationDropdown.css";

const LocationDropdown = ({
  label,
  required = false,
  value = "",
  placeholder = "Pilih data",
  searchPlaceholder,
  options = [],
  loading = false,
  disabled = false,
  error = "",
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  const hasOptions = Array.isArray(options) && options.length > 0;
  const showInitialLoading = loading && !hasOptions;
  const showRefreshingState = loading && hasOptions;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setQuery("");
    }
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;

    return options.filter((item) => {
      const haystack = `${item.name || ""} ${item.code || ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  const handleSelect = (item) => {
    onSelect?.(item);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="sell-location-field">
      <label className="mp-label">
        {label} {required && <span className="required">*</span>}
      </label>

      <div className="sell-dropdown" ref={wrapperRef}>
        <button
          type="button"
          className={`sell-dropdown__button ${error ? "error" : ""} ${
            showInitialLoading ? "is-loading" : ""
          }`}
          onClick={() => !disabled && setOpen((prev) => !prev)}
          disabled={disabled}
          aria-expanded={open}
          aria-busy={loading}
        >
          <span
            className={`sell-dropdown__value ${value ? "" : "placeholder"}`}
          >
            {value || placeholder}
          </span>

          <span className={`sell-dropdown__icon ${open ? "open" : ""}`}>
            <MdKeyboardArrowDown size={20} />
          </span>
        </button>

        {open && (
          <div className="sell-dropdown__panel" aria-live="polite">
            {showInitialLoading ? (
              <div className="sell-dropdown__loading-state">
                <div className="sell-dropdown__loading-head">
                  <div className="sell-dropdown__loading-shimmer sell-dropdown__loading-shimmer--search" />
                </div>

                <div className="sell-dropdown__loading-list">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="sell-dropdown__loading-item">
                      <div className="sell-dropdown__loading-lines">
                        <span className="sell-dropdown__loading-shimmer sell-dropdown__loading-shimmer--title" />
                        <span className="sell-dropdown__loading-shimmer sell-dropdown__loading-shimmer--subtitle" />
                      </div>
                      <span className="sell-dropdown__loading-shimmer sell-dropdown__loading-shimmer--code" />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="sell-dropdown__search">
                  <MdSearch size={18} />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={
                      searchPlaceholder || `Cari ${label.toLowerCase()}...`
                    }
                  />
                  {query && (
                    <button
                      type="button"
                      className="sell-dropdown__clear"
                      onClick={() => setQuery("")}
                      aria-label="Hapus pencarian"
                    >
                      <MdClose size={18} />
                    </button>
                  )}
                </div>

                {showRefreshingState && (
                  <div className="sell-dropdown__refreshing-note">
                    Memperbarui data...
                  </div>
                )}

                <div className="sell-dropdown__list">
                  {filteredOptions.length ? (
                    filteredOptions.map((item) => (
                      <button
                        key={item.code}
                        type="button"
                        className="sell-dropdown__option"
                        onClick={() => handleSelect(item)}
                      >
                        <span className="sell-dropdown__option-name">
                          {item.name}
                        </span>
                        <span className="sell-dropdown__option-code">
                          {item.code}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="sell-dropdown__state">
                      Tidak ada data yang sesuai.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {error && <span className="sell-field-error">{error}</span>}
    </div>
  );
};

export default LocationDropdown;
